import React, { useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { FontFamily } from '../../../../constants/fonts';

import TrendSection from '../../components/TrendSection';

import type {
  CategoriesBreakdownRowData,
  CategoriesTopSubCategoryData,
  CategoriesTrendRowData,
  TicketingCategoriesPayload,
  TicketingRcaPayload,
} from '../../components/types';

import { KPIFilterDropdownItem } from '../../../AnalyticsHub/components/KPIFilterDropdown';

import VerticalBarChartCard from '../../components/VerticalBarChartCard';

import AnalyticsChartCard from '../../components/AnalyticsChartCard';

interface CategoriesProps {
  loading: boolean;
  error: string | null;
  data: TicketingCategoriesPayload | null;
  breakdownView: 'total' | 'open';
  onBreakdownViewChange: (view: 'total' | 'open') => void;
  selectedCategory: number | null;
  onCategorySelect: (categoryId: number | null) => void;
  topSubView: 'total' | 'open';
  onTopSubViewChange: (view: 'total' | 'open') => void;
  /** Categories selected in the filter dropdown (empty = show all) */
  selectedCategoryFilters?: KPIFilterDropdownItem[];
  /** RCA data for level-3 drill-down (sub-category → RCA breakdown) */
  rcaData?: TicketingRcaPayload | null;
  onRetry?: () => void;
  /** When true, shows the filter-hint empty message instead of the generic one. */
  hasActiveFilters?: boolean;
}

const Categories: React.FC<CategoriesProps> = ({
  loading,
  error,
  data,
  breakdownView,
  onBreakdownViewChange,
  selectedCategory,
  onCategorySelect,
  topSubView,
  onTopSubViewChange,
  selectedCategoryFilters,
  rcaData,
  onRetry,
  hasActiveFilters = false,
}) => {
  // Level 3: selected sub-category name for RCA breakdown drill-down
  const [selectedSubCategory, setSelectedSubCategory] = useState<string | null>(null);
  /**
   * Names of categories chosen in the filter dropdown.
   * When the array is empty or only contains the "All" sentinel, every
   * category is shown (no filtering applied).
   */
  const activeFilterNames = useMemo<Set<string>>(() => {
    const items = selectedCategoryFilters ?? [];
    const meaningful = items.filter(
      (item) => String(item.id).toLowerCase() !== 'all',
    );
    return new Set(meaningful.map((item) => item.displayText));
  }, [selectedCategoryFilters]);

  const breakdownRows = useMemo((): CategoriesBreakdownRowData[] => {
    const breakdown = data?.categoryBreakdown;

    if (!breakdown) {
      return [];
    }

    const base =
      breakdownView === 'open'
        ? breakdown.open?.categories ?? []
        : breakdown.total?.categories ?? [];

    if (!base || base.length === 0) {
      return [];
    }

    // Level 1 (no category selected): list top-level categories
    if (selectedCategory == null) {
      const rows = base.map((item) => ({
        id: item.id,
        name: item.name,
        count: item.count,
        percentage: item.percentage,
      }));

      // Apply dropdown filters when provided
      if (activeFilterNames.size > 0) {
        return rows.filter((item) => activeFilterNames.has(item.name));
      }

      return rows;
    }

    // Level 2 (category selected): return its sub-categories
    const parent = base.find((item) => item.id === selectedCategory);
    if (!parent || !parent.subCategories) return [];

    return parent.subCategories.map((item) => ({
      id: item.id,
      name: item.name,
      count: item.count,
      percentage: item.percentage,
    }));
  }, [data?.categoryBreakdown, breakdownView, selectedCategory, activeFilterNames]);

  const selectedCategoryLabel = useMemo(() => {
    if (selectedCategory == null) {
      return null;
    }

    const breakdown = data?.categoryBreakdown;

    if (!breakdown) {
      return null;
    }

    const base =
      breakdownView === 'open'
        ? breakdown.open.categories
        : breakdown.total.categories;

    const parent = base.find((item) => item.id === selectedCategory);

    return parent?.name ?? null;
  }, [data?.categoryBreakdown, breakdownView, selectedCategory]);

  const topSubRows = useMemo<CategoriesTopSubCategoryData[]>(() => {
    const source = data?.topSubCategories;

    if (!source) {
      return [];
    }

    const rows = topSubView === 'open' ? source.open : source.total;

    // Deduplicate by unique (category + name) key, then take top 5 by count
    const deduped = new Map<string, { name: string; category: string; count: number; percentage: number }>();
    rows.forEach((item) => {
      const key = `${item.category}|||${item.name}`;
      const existing = deduped.get(key);
      if (!existing || item.count > existing.count) {
        deduped.set(key, item);
      }
    });

    return Array.from(deduped.values())
      .sort((a, b) => b.count - a.count) // Sort by count descending
      .slice(0, 5) // Strictly limit to top 5 records
      .map((item) => ({
        name: item.name,
        category: item.category,
        count: item.count,
        percentage: item.percentage,
      }));
  }, [data?.topSubCategories, topSubView]);

  const trendRows = useMemo<CategoriesTrendRowData[]>(() => {
    const categoryIdByName = new Map(
      (data?.categoryBreakdown?.total?.categories ?? []).map((item) => [
        item.name,
        item.id,
      ]),
    );

    const mappedRows = (data?.categoryTrend ?? []).map((item) => ({
      categoryId: categoryIdByName.get(item.category),
      category: item.category,
      percentageChange: item.percentageChange,
      weeks: item.weeks.map((week) => ({
        label: week.label,
        value: week.value,
      })),
      subCategories: item.subCategories?.map((sub) => ({
        name: sub.name,
        percentageChange: sub.percentageChange,
        weeks: sub.weeks.map((week) => ({
          label: week.label,
          value: week.value,
        })),
      })),
    }));
    
    console.log('[Categories Component] Trend rows with subcategories:', mappedRows.map(r => `${r.category}: ${r.subCategories?.length ?? 0} subs`));
    return mappedRows;
  }, [data?.categoryBreakdown?.total?.categories, data?.categoryTrend]);

  // Build RCA breakdown for selected sub-category (level 3)
  const rcaBreakdownRows = useMemo(() => {
    if (!selectedSubCategory || !rcaData) return [];
    const categoryLabel = selectedCategoryLabel ?? '';

    // Find RCA counts for this specific sub-category across all RCA categories
    const rows: { label: string; value: number; percentage: number }[] = [];
    const totalForSubCategory = rcaData.rcaByCategory?.matrix?.reduce((sum, matrixRow) => {
      const subCatMap = rcaData.rcaByCategory?.subCategoryMatrix?.find(
        (s) => s.rcaCategory === matrixRow.rcaCategory
      )?.categorySubCategoryValues?.[categoryLabel]?.[selectedSubCategory] ?? 0;
      return sum + subCatMap;
    }, 0) ?? 0;

    rcaData.rcaByCategory?.rcaCategories?.forEach((rcaCategory) => {
      const subCatMatrix = rcaData.rcaByCategory?.subCategoryMatrix?.find(
        (s) => s.rcaCategory === rcaCategory
      );
      const count = subCatMatrix?.categorySubCategoryValues?.[categoryLabel]?.[selectedSubCategory] ?? 0;
      if (count > 0) {
        rows.push({
          label: rcaCategory,
          value: count,
          percentage: totalForSubCategory > 0 ? (count / totalForSubCategory) * 100 : 0,
        });
      }
    });

    return rows.sort((a, b) => b.value - a.value);
  }, [selectedSubCategory, selectedCategoryLabel, rcaData]);

  return (
    <View style={styles.content}>
      {/*
        No blocking "Loading..." text — each card below renders its own
        skeleton via its `loading` prop so the layout stays stable.
      */}
    <VerticalBarChartCard
      title={
        selectedSubCategory
          ? `${selectedSubCategory} — RCA Breakdown`
          : selectedCategoryLabel
          ? `${selectedCategoryLabel} Sub-Categories`
          : 'Category Breakdown'
      }
      subtitle={
        selectedSubCategory
          ? 'RCA category distribution for selected sub-category'
          : 'Select a category to view sub categories'
      }
      data={
        selectedSubCategory
          ? rcaBreakdownRows.map(item => ({
              label: item.label,
              value: item.value,
              valueLabel: item.value.toLocaleString(),
              percentage: item.percentage,
            }))
          : breakdownRows.map(item => ({
              id: item.id,
              label: item.name,
              value: item.count,
              valueLabel: item.count.toLocaleString(),
              percentage: item.percentage,
            }))
      }
      metadataPosition="inside"
      loading={loading}
      skeletonBarCount={7}
      toggleValue={selectedSubCategory ? undefined : breakdownView}
      toggleOptions={selectedSubCategory ? undefined : ['total', 'open']}
      onToggleChange={selectedSubCategory ? undefined : onBreakdownViewChange}
      showBackButton={!!selectedCategoryLabel || !!selectedSubCategory}
      onBack={() => {
        if (selectedSubCategory) {
          // Go back from level 3 to level 2
          setSelectedSubCategory(null);
        } else {
          // Go back from level 2 to level 1
          onCategorySelect(null);
        }
      }}
      onBarPress={
        selectedSubCategory
          ? undefined // Level 3: no further drill-down
          : selectedCategory == null
          ? (item => onCategorySelect(item.id as number)) // Level 1 → Level 2
          : (item => {
              // Level 2 → Level 3: only drill down if RCA data exists for this sub-category
              const categoryLabel = selectedCategoryLabel ?? '';
              const subCatName = item.label;
              const hasRcaData = rcaData?.rcaByCategory?.subCategoryMatrix?.some(
                (s) => (s.categorySubCategoryValues?.[categoryLabel]?.[subCatName] ?? 0) > 0
              );
              if (hasRcaData) {
                setSelectedSubCategory(subCatName);
              }
            })
      }
      minHeight={280}
      error={error}
      onRetry={onRetry}
      hasActiveFilters={hasActiveFilters}
    />

        <AnalyticsChartCard
          title="Top Sub Categories"
          chartType="horizontal-bar"
          loading={loading}
          skeletonRowCount={5}
          error={error}
          onRetry={onRetry}
          hasActiveFilters={hasActiveFilters}
          toggleValue={topSubView}
          toggleOptions={[
            {
              key: 'total',
              label: 'Total',
            },
            {
              key: 'open',
              label: 'Open',
            },
          ]}
          onToggleChange={(value) =>
              onTopSubViewChange(value as 'total' | 'open')
            }
          rows={topSubRows.map(
            item => ({
              label: item.name,
              subLabel:
                item.category,
              value: item.count,
              percentage:
                item.percentage,
            }),
          )}
        />

      <TrendSection
        rows={trendRows}
        loading={loading}
        selectedCategoryLabel={selectedCategoryLabel}
        onCategorySelect={onCategorySelect}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  content: {
    padding: 2,
  },
  metaError: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#B42318',
    marginTop: 6,
    marginBottom: 8,
  },
});

export default Categories;